import type { ExecutionContext, ExportBucket, ExportRepository, ExportRow } from "@dker/core";
import { DomainError } from "@dker/core";
import type { DbClientFactory } from "../ports.js";

/**
 * Lecture de tout ce que l'export emporte, avec la session du patron.
 *
 * La RLS fait le cloisonnement : pas de clé de service, pas de fonction Edge.
 * Le filtre par entreprise écrit en plus n'est pas une seconde garde, c'est
 * une clarté — il dit ce qu'on lit, et rend la requête juste même pour une
 * session qui verrait plus large.
 */

/** Le plafond de lignes que PostgREST rend par requête. */
const PAGE_SIZE = 1000;

/** Le plafond d'objets que le Storage liste par appel. */
const LIST_PAGE_SIZE = 1000;

function fail(table: string, error: { message: string; code?: string }): never {
  throw new DomainError("conflict", `Lecture impossible de « ${table} » : ${error.message}`, {
    pgCode: error.code,
  });
}

export function createSupabaseExportRepository(clientFor: DbClientFactory): ExportRepository {
  return {
    async readTable(
      ctx: ExecutionContext,
      table: string,
      key: readonly string[],
    ): Promise<ExportRow[]> {
      const scope = table === "tenants" ? "id" : "tenant_id";
      const rows: ExportRow[] = [];

      // Page après page jusqu'à une page incomplète. S'arrêter à la première
      // rendrait, au-delà de mille lignes, une archive tronquée qui aurait
      // l'air entière — le défaut qu'aucun patron ne verrait avant d'en avoir
      // besoin.
      for (let from = 0; ; from += PAGE_SIZE) {
        let query = clientFor(ctx).from(table).select("*").eq(scope, ctx.tenantId);
        for (const column of key) query = query.order(column, { ascending: true });
        const { data, error } = await query.range(from, from + PAGE_SIZE - 1);

        if (error) fail(table, error);
        const page = (data ?? []) as ExportRow[];
        rows.push(...page);
        if (page.length < PAGE_SIZE) return rows;
      }
    },

    async listObjects(ctx: ExecutionContext, bucket: "documents" | "branding"): Promise<string[]> {
      const storage = clientFor(ctx).storage.from(bucket);
      const paths: string[] = [];
      // Le Storage ne liste qu'un dossier à la fois : on descend.
      const folders = [ctx.tenantId];

      while (folders.length > 0) {
        const folder = folders.shift()!;
        for (let offset = 0; ; offset += LIST_PAGE_SIZE) {
          const { data, error } = await storage.list(folder, {
            limit: LIST_PAGE_SIZE,
            offset,
            sortBy: { column: "name", order: "asc" },
          });
          if (error) fail(`${bucket}/${folder}`, error);

          const items = data ?? [];
          for (const item of items) {
            // Un dossier n'a pas d'identifiant : c'est un préfixe, pas un objet.
            if (item.id === null) folders.push(`${folder}/${item.name}`);
            else paths.push(`${folder}/${item.name}`);
          }
          if (items.length < LIST_PAGE_SIZE) break;
        }
      }
      return paths;
    },

    async download(
      ctx: ExecutionContext,
      bucket: ExportBucket,
      path: string,
    ): Promise<Uint8Array | null> {
      // Un objet qui ne se télécharge pas est compté manquant par le service,
      // pas une raison de priver le patron de tout le reste de l'archive.
      try {
        const { data, error } = await clientFor(ctx).storage.from(bucket).download(path);
        if (error || !data) return null;
        return new Uint8Array(await data.arrayBuffer());
      } catch {
        return null;
      }
    },
  };
}
